/** @jsxImportSource @emotion/react */
import { Fragment } from 'react'
import PropTypes from 'prop-types'
import { jsx } from '@emotion/react'
import css from '@emotion/css/macro'
import { Link } from 'react-router-dom'
import { getImageThumbnailId } from '@sportninja/common/reducers/helpers'
import { t } from '@sportninja/common/i18n'

import Picture from '../Picture'
import {
  DEFAULT_COL_1_WIDTH,
  DEFAULT_COL_1_WIDTH_MOBILE,
} from './StatisticsWrapper'
import { InitialsThumbnail } from '../ThumbnailAndName'

const entityCell =
  (type, hideImage) =>
  // eslint-disable-next-line react/display-name
  ({ isMobile, row, rowNumber, value }) => {
    if (row.isGrouped) return ''
    const isTeamType = type === 'team'
    let item = isTeamType ? row.original.team : row.original.player
    if (!item) item = {}
    const imageId = getImageThumbnailId(item)

    return (
      <Link
        style={{ display: 'flex', alignItems: 'center' }}
        to={`/${type}/${item.id}`}
        css={css`
          .team-logo {
            z-index: 1;
          }
        `}
      >
        {type === 'player' && (
          <div
            className='statistics-player-row-number'
            css={css`
              font-weight: bold;
              margin-right: 10px;
              min-width: 20px;
              text-align: center;
              color: white;
            `}
          >
            {rowNumber}
          </div>
        )}
        {!isMobile &&
          !hideImage &&
          (imageId || isTeamType ? (
            <Picture
              imageId={imageId}
              size='xxsmall'
              square={isTeamType}
              fit={isTeamType}
              iconName={isTeamType ? 'user-friends' : undefined}
              className={
                type === 'player'
                  ? 'player-portrait'
                  : isTeamType
                  ? 'team-logo'
                  : ''
              }
            />
          ) : (
            <div
              css={css`
                margin-right: 8px;
              `}
            >
              <InitialsThumbnail size={34}>
                {item?.name_first?.charAt(0)}
                {item?.name_last?.charAt(0)}
              </InitialsThumbnail>
            </div>
          ))}
        <span
          css={css`
            margin-left: ${(imageId || isTeamType) && !hideImage ? 8 : 0}px;
          `}
        >
          {value}
        </span>
      </Link>
    )
  }

const entitySeason = ({ row, value }) => {
  if (!row || !row.original || !row.original.season) {
    if (typeof value === 'undefined') return false
    return value
  }

  return (
    <Link
      style={{ display: 'flex', alignItems: 'center' }}
      to={`/schedule/${row.original.season.id}`}
    >
      {value}
    </Link>
  )
}

entitySeason.propTypes = {
  row: PropTypes.object,
  value: PropTypes.string,
}

const getNameText = (accessor, row) => {
  if (!row || !accessor || !Object.prototype.hasOwnProperty.call(row, accessor))
    return ''
  const rowItem = row[accessor] || {}

  switch (accessor) {
    case 'team':
      return rowItem.name_full || ''

    case 'player': {
      return (
        <Fragment>
          {/* We need to explicitly use these values, in case of underage players (where `full_name` is not in response...) */}
          <div>
            {rowItem.name_first} {rowItem.name_last}
          </div>
          {(rowItem.player_number || rowItem.isAffiliate) && (
            <div
              css={css`
                opacity: 0.6;
                font-size: 14px;
                margin-top: 4px;
                text-transform: capitalize;
              `}
            >
              {/* Removed as requested on the comments of: https://sportninja.atlassian.net/browse/SN-1888 */}
              {/* {rowItem.player_number ? `#${rowItem.player_number}` : ''}
              {rowItem.player_number && rowItem.isAffiliate ? ' - ' : ''} */}
              {rowItem.isAffiliate && t('common:affiliate')}
            </div>
          )}
        </Fragment>
      )
    }

    default:
      return ''
  }
}

/**
 * @param {string} type - Either 'team' or 'player'
 * @param {function} sortType - callback for sorting
 */
const getNameColumn = (type, sortType) => {
  const settings = {
    id: 'name',
    width: DEFAULT_COL_1_WIDTH,
    mobileWidth: DEFAULT_COL_1_WIDTH_MOBILE,
    Header: t(`common:${type}`),
    accessor: getNameText.bind(this, type),
    Cell: entityCell(type),
    sortDescFirst: true,
  }

  if (sortType) settings.sortType = sortType
  return settings
}

const getTeamColumn = () => ({
  id: 'Team',
  width: DEFAULT_COL_1_WIDTH,
  mobileWidth: DEFAULT_COL_1_WIDTH_MOBILE,
  Header: t('util:uppercase', { text: t('common:team') }),
  accessor: 'team.name_full',
  Cell: entityCell('team'),
})

export default {
  entityCell,
  entitySeason,
  getNameText,
  getNameColumn,
  getTeamColumn,
}
